import { useMemo } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useQueryGetProductivityRiskTrend } from 'api/queries/getProductivityRiskTrend.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';

export type UseProductivityRiskTrendProvider = ReturnType<typeof useProductivityRiskTrendProvider>;

interface Props {
    componentKey: string;
    widgetId?: string;
    latestVersionId?: number | undefined;
    compareVersionId?: number | undefined;
    globalFilterData: any;
    localFilters: any;
    contractId: number | null | undefined;
    endpoints: Record<string, string>;
}

export const useProductivityRiskTrendProvider = ({
    widgetId,
    latestVersionId,
    localFilters,
    globalFilterData,
    compareVersionId,
    componentKey,
    contractId,
    endpoints,
}: Props) => {
    const { isGenerativeAi, reportId } = usePinboardWidgetWrapperContext();
    const source = Boolean(widgetId) ? localFilters : { ...globalFilterData };

    const filters = useMemo(() => {
        return getFiltersData({
            [FilterNames.ACTIVITY_CODES]: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
            [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                ?.filter((item) => item.isExclude)
                .map((i) => i.name),
            [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
            [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
            [FilterNames.WBS_SEARCH]: source?.wbs,
        });
    }, [source]);

    const getProductivityRiskTrendPayload = {
        compareVersionId,
        latestVersionId,
        contractId,
        filter: {
            filters,
        },
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId, reportId),
    };

    const getProductivityRiskTrend = useQueryGetProductivityRiskTrend(
        getProductivityRiskTrendPayload,
        endpoints.getProductivityRiskTrend,
    );

    const pillsData = {
        activityCodes: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
        wbs: source?.wbs,
    };

    const { pillsList: pills } = useGlobalFiltersHook({ source: pillsData, latestVersionId });

    return {
        ui: {
            state: {
                activityCodes: source?.activityCodes,
                milestoneFragnet: source?.milestoneFragnet,
                tags: source?.tags,
                wbs: source?.wbs,
            },
            pills,
        },
        api: {
            getProductivityRiskTrend: {
                instance: getProductivityRiskTrend,
                payload: getProductivityRiskTrendPayload,
                endpoint: endpoints.getProductivityRiskTrend,
            },
        },
    };
};
